import * as React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clsx } from "clsx";
import { GripVertical } from "lucide-react";
import { SNAPSHOT_GRID_DATA_COLUMN_WIDTH, getSnapshotGridColumnStyle } from "./layout";
import { ColumnResizeHandle } from "./columnResizeHandle";

interface Props {
  columnId: string;
  children: React.ReactNode;
  width?: number;
  onResize?: (columnId: string, width: number) => void;
}

export const DraggableColumnHeader: React.FC<Props> = ({
  columnId,
  children,
  width = SNAPSHOT_GRID_DATA_COLUMN_WIDTH,
  onResize,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: columnId,
  });
  const style: React.CSSProperties = {
    ...getSnapshotGridColumnStyle(width),
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 30,
  };

  const resizeLabel = columnId.startsWith("note:") ? columnId.slice("note:".length) : columnId;

  return (
    <th
      ref={setNodeRef}
      scope="col"
      className={clsx(
        "sticky top-0 overflow-hidden bg-[#f1edea] px-3 py-3.5 pr-4 text-left text-sm font-semibold text-gray-900",
        isDragging && "opacity-50 shadow-lg",
      )}
      style={style}
    >
      <div className="group/header flex min-w-0 items-center gap-1">
        <button
          className="mr-1 cursor-grab touch-none rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        {children}
      </div>
      {onResize && <ColumnResizeHandle columnId={columnId} label={resizeLabel} width={width} onResize={onResize} />}
    </th>
  );
};
